import { FC, JSX, useContext, useRef, useState } from "react";
import { ReportStatusConverter, ReportsUIModel } from "./reportsModel";
import { ReportsTileCollapseContext } from "./ReportsTileCollapseContext";
import { ExportService } from "@app/exports";
import { QESButton, QESAlert } from "@app/shared/qes-standards";
import { Modal } from "@app/shared/Modals";
import { Link } from "react-router-dom";

interface ReportTileProps {
  report: ReportsUIModel;
  parentSaysExpand?: boolean;
  refreshReports: () => void;
  isYourReports: boolean;
}

export const ReportTile: FC<React.PropsWithChildren<ReportTileProps>> = (props: ReportTileProps) => {
  const { collapseTile, expandedTiles, expandTile } = useContext(ReportsTileCollapseContext);
  const [error, setError] = useState<JSX.Element>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isSharedWithExpanded, setIsSharedWithExpanded] = useState<boolean>(false);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const exportService = new ExportService();

  const report = props.report;
  const isExpanded = expandedTiles.includes(report.id);
  const showCancelReport = (reportStatus: ReportStatusConverter) => {
    switch (reportStatus) {
      case ReportStatusConverter.processing:
      case ReportStatusConverter.queued:
        return true;
    }
    return false;
  }

  const cancelReport = () => {
    exportService.cancelReportJob(report.id).then(() => {
      props.refreshReports();
    }, (reject: unknown) => {
      setError(<QESAlert type="danger">{(reject as { responseText?: string }).responseText}</QESAlert>);
    })
  }

  const cancelReportQuestion = "Are you sure you want to cancel this report?";

  const [displayedShareWith, setDisplayedShareWith] = useState<string[]>(props.report.sharedWith?.slice(0, 3) ?? []);

  const getSharedWith = () => {
    return (
      <>
        <ul style={{ listStyleType: "none", margin: 0, padding: 0 }}>
          {displayedShareWith.map((user, index, arr) => (
            <li key={user} style={{ width: "90%" }}>
              {user}
              {(report.sharedWith?.length > 3 && ((isSharedWithExpanded && arr.length - 1 === index) || (!isSharedWithExpanded && index === 2))) &&
                <span style={{ float: "right" }}>
                  <QESButton className="p-0" qesType="secondary" qesTestId={`reports-tile__expand--shared-with`} onClick={() => toggleSharedWith()}>{isSharedWithExpanded ? "View less" : "View all"}</QESButton>
                </span>}
            </li>)
          )}
        </ul>
      </>
    );
  }

  const toggleSharedWith = () => {
    if (isSharedWithExpanded)
      setDisplayedShareWith(report.sharedWith.slice(0, 3));
    else
      setDisplayedShareWith(report.sharedWith);
    setIsSharedWithExpanded(!isSharedWithExpanded);
  }

  return (
    <div className="report-tile col-md-6 col-12">
      <dl>
        <div>
          <dt>{(props.isYourReports && report.sharedWith?.length > 0) && <span style={{ float: "left" }}>Shared</span>}Report Name:</dt>
          <dd>{report.reportName}</dd>
        </div>
        <div>
          <dt>Type:</dt>
          <dd>{report.exportType}</dd>
        </div>
        <div>
          <dt>As Of:</dt>
          <dd>{report.asOfUtc}</dd>
        </div>
        <div>
          <dt>Project Title:</dt>
          {(report.projectId && report.projectId > 0) ? <dd><Link to={{ pathname: `/projects/${report.projectId}` }}>{report.projectName}</Link></dd> : <dd>{report.projectName}</dd>}
        </div>
        <div>
          <dt>Project Type:</dt>
          <dd>{report.projectType}</dd>
        </div>
        <div>
          <dt>Format:</dt>
          <dd>{report.fileType}</dd>
        </div>
        {isExpanded && (
          <>
            <div>
              <dt>Last Requested:</dt>
              <dd>{report.createDateUtc}</dd>
            </div>
            <div>
              <dt>Service Area Type:</dt>
              <dd>{report.serviceAreaType}</dd>
            </div>
            <div>
              <dt>Include Active Counties:</dt>
              <dd>{report.includeActiveRegionSets}</dd>
            </div>
            <div>
              <dt>Include Approved Counties:</dt>
              <dd>{report.includeApprovedRegionSets}</dd>
            </div>
            <div>
              <dt>Include Pending Counties:</dt>
              <dd>{report.includePendingRegionSets}</dd>
            </div>
            <div>
              <dt>Include Physician Specialties:</dt>
              <dd>{report.includePhysicianSpecialties}</dd>
            </div>
            <div>
              <dt>Include Facility Specialties:</dt>
              <dd>{report.includeFacilitySpecialties}</dd>
            </div>
            <div>
              <dt>Specialty Filter:</dt>
              <dd>{report.specialtyFilter}</dd>
            </div>
            <div>
              <dt>Providers Category:</dt>
              <dd>{report.providerCategory}</dd>
            </div>
            <div>
              <dt>HSD:</dt>
              <dd>{report.isHsd}</dd>
            </div>
            <div>
              <dt>Preset Used:</dt>
              <dd>{report.presetName}</dd>
            </div>
            <div>
              <dt>Analysis:</dt>
              <dd>{report.analysisType}</dd>
            </div>
            {(!props.isYourReports && report.sharedBy) &&
              <div>
                <dt>Shared By:</dt>
                <dd>{report.sharedBy}</dd>
              </div>}
            {(props.isYourReports && report.sharedWith?.length > 0) &&
              <div>
                <dt>Shared With:</dt>
                <dd>
                  {getSharedWith()}
                </dd>
              </div>}
          </>
        )}
        <div>
          <dt>Status:</dt>
          <dd>{report.reportStatus}</dd>
        </div>
        <div className={showCancelReport(report.reportStatusEnum) ? "text-danger" : "hidden"}>
          <dt>&nbsp;</dt>
          <dd>
            <QESButton
              className="p-0 font-weight-normal"
              qesType="secondary-danger"
              qesTestId={`cancel-report-${report.id}`}
              onClick={() => setShowModal(true)}
              qesRef={cancelButtonRef}
            >
              <i className="fa fa-close mr-1" />Cancel Report
            </QESButton>
          </dd>
        </div>
      </dl>
      <QESButton
        qesType="secondary"
        className="p-0 reports-expansion-toggle text-decoration-none"
        qesTestId={`report-tile__toggle-expand--${report.id}`}
        onClick={() => isExpanded ? collapseTile(report.id) : expandTile(report.id)}
      >
        <i className={`d-block fa ${isExpanded ? "fa-minus" : "fa-plus"}`} />
      </QESButton>
      {error || error}
      <Modal
        closeModal={() => { setShowModal(false) }}
        content={{
          body: cancelReportQuestion,
          cancelButtonText: "Exit",
          confirmButtonAction: () => { cancelReport(); setShowModal(false); },
          confirmButtonText: "Yes, cancel report",
          confirmButtonType: "primary-danger",
          title: "Cancel Report",
        }}
        isVisible={showModal}
        hideBodyScroll={true}
        triggerElement={cancelButtonRef}
      />
    </div>
  );
}
